import React, { useState, useMemo } from "react";
import "./PointsCalculator.css";

// Exact same formula used in UploadCompetition.js
function calculatePoints(place, participants) {
  const raw =
    321 * Math.exp(-0.077 * place) * (0.39 * Math.log(participants) + 0.13);
  return Math.round(raw);
}

function finalPoints(place, participants, multiplier) {
  return Math.round(calculatePoints(place, participants) * multiplier);
}

const MULTIPLIERS = [
  { label: "×1 — Ranking Competition", value: 1 },
  { label: "×1.5 — Grand Final Ranking Competition", value: 1.5 },
];

const PLACE_MEDALS = ["🥇", "🥈", "🥉"];

function getPlaceLabel(n) {
  if (n === 1) return "1st";
  if (n === 2) return "2nd";
  if (n === 3) return "3rd";
  return `${n}th`;
}

function getResultColor(pts, maxPts) {
  const ratio = pts / maxPts;
  if (ratio >= 0.85) return "#FFD700";
  if (ratio >= 0.65) return "#C0C0C0";
  if (ratio >= 0.45) return "#CD7F32";
  if (ratio >= 0.25) return "#6db5f7";
  return "#aaa";
}

export default function PointsCalculator() {
  const [participants, setParticipants] = useState(10);
  const [place, setPlace] = useState(1);
  const [multiplierVal, setMultiplierVal] = useState(1);
  const [highlighted, setHighlighted] = useState(false);

  // Clamp place when participants changes
  const clampedPlace = Math.min(place, participants);

  const result = finalPoints(clampedPlace, participants, multiplierVal);
  const maxResult = finalPoints(1, participants, multiplierVal);

  // Build full breakdown table
  const breakdown = useMemo(() => {
    return Array.from({ length: participants }, (_, i) => {
      const p = i + 1;
      const pts = finalPoints(p, participants, multiplierVal);
      return { place: p, pts };
    });
  }, [participants, multiplierVal]);

  function handleParticipantsChange(val) {
    setParticipants(val);
    if (place > val) setPlace(val);
  }

  function handlePlaceChange(val) {
    const clamped = Math.max(1, Math.min(val, participants));
    setPlace(clamped);
    triggerHighlight();
  }

  function handleMultiplierChange(val) {
    setMultiplierVal(val);
    triggerHighlight();
  }

  function triggerHighlight() {
    setHighlighted(true);
    setTimeout(() => setHighlighted(false), 600);
  }

  const resultPercent = maxResult > 0 ? (result / maxResult) * 100 : 0;

  return (
    <div className="pc-page">
      <h1 className="pc-title">Points Calculator</h1>

      <div className="pc-main">
        {/* LEFT: Controls */}
        <div className="pc-controls">

          {/* Participants */}
          <div className="pc-field">
            <label className="pc-label">
              Number of Participants
              <span className="pc-label-val">{participants}</span>
            </label>
            <input
              type="range"
              className="pc-slider"
              min={2}
              max={60}
              value={participants}
              style={{ "--val": `${((participants - 2) / 58) * 100}%` }}
              onChange={(e) => handleParticipantsChange(Number(e.target.value))}
            />
            <div className="pc-slider-bounds">
              <span>2</span>
              <span>60</span>
            </div>
          </div>

          {/* Place */}
          <div className="pc-field">
            <label className="pc-label">
              Place
              <span className="pc-label-val">
                {clampedPlace <= 3 ? PLACE_MEDALS[clampedPlace - 1] + " " : ""}
                {getPlaceLabel(clampedPlace)}
              </span>
            </label>
            <input
              type="range"
              className="pc-slider"
              min={1}
              max={participants}
              value={clampedPlace}
              style={{ "--val": `${((clampedPlace - 1) / Math.max(1, participants - 1)) * 100}%` }}
              onChange={(e) => handlePlaceChange(Number(e.target.value))}
            />
            <div className="pc-slider-bounds">
              <span>1st</span>
              <span>{getPlaceLabel(participants)}</span>
            </div>
          </div>

          {/* Multiplier */}
          <div className="pc-field">
            <label className="pc-label">
              Competition Type
            </label>
            <div className="pc-multiplier-grid">
              {MULTIPLIERS.map((m) => (
                <button
                  key={m.value}
                  className={`pc-mult-btn${multiplierVal === m.value ? " active" : ""}`}
                  onClick={() => handleMultiplierChange(m.value)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Result */}
        <div className={`pc-result-panel${highlighted ? " pulse" : ""}`}>
          <div className="pc-result-label">Points Earned</div>
          <div className="pc-result-number" style={{ color: getResultColor(result, maxResult) }}>
            {result.toLocaleString()}
          </div>
          <div className="pc-result-detail">
            {getPlaceLabel(clampedPlace)} out of {participants} &nbsp;·&nbsp; {MULTIPLIERS.find(m => m.value === multiplierVal)?.label}
          </div>

          {/* Progress bar */}
          <div className="pc-bar-wrap">
            <div
              className="pc-bar-fill"
              style={{
                width: `${resultPercent}%`,
                background: getResultColor(result, maxResult),
              }}
            />
          </div>
          <div className="pc-bar-labels">
            <span>0</span>
            <span>Max: {maxResult.toLocaleString()} pts</span>
          </div>

          {/* Formula explainer */}
          <div className="pc-formula">
            <div className="pc-formula-title">How it's calculated:</div>
            <div className="pc-formula-line">
              Base = 321 × e<sup>−0.077 × place</sup> × (0.39 × ln(participants) + 0.13)
            </div>
            <div className="pc-formula-line pc-formula-final">
              Final = round(Base × {multiplierVal}) = <strong>{result}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Full Breakdown Table */}
      <div className="pc-breakdown">
        <h2 className="pc-breakdown-title">
          Full Breakdown — {participants} Participants · ×{multiplierVal}
        </h2>
        <div className="pc-table-wrap">
          <table className="pc-table">
            <thead>
              <tr>
                <th>Place</th>
                <th>Points</th>
                <th>Bar</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map(({ place: p, pts }) => (
                <tr
                  key={p}
                  className={p === clampedPlace ? "pc-table__row--active" : ""}
                  onClick={() => handlePlaceChange(p)}
                >
                  <td className="pc-table__place">
                    {p <= 3 ? <span className="pc-medal">{PLACE_MEDALS[p - 1]}</span> : null}
                    {getPlaceLabel(p)}
                  </td>
                  <td className="pc-table__pts">{pts.toLocaleString()}</td>
                  <td className="pc-table__bar">
                    <div
                      className="pc-mini-bar"
                      style={{
                        width: `${(pts / maxResult) * 100}%`,
                        background: getResultColor(pts, maxResult),
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="pc-table-hint">Click any row to update the calculator above.</p>
      </div>
    </div>
  );
}
